"use strict";

if (!customElements.get("product-item-slider")) {
  customElements.define("product-item-slider", class ProductItemSlider extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.prevBtn = null;
      this.nextBtn = null;
      this.observer = null;
      this.slideWidth = 0;
      this.loadTimers = new Map();
    }
    connectedCallback() {
      this.slider = this.querySelector(".js-slider");
      this.prevBtn = this.querySelector(".prev");
      this.nextBtn = this.querySelector(".next");
      if (!this.slider || !this.prevBtn || !this.nextBtn) return;
      const slides = this.slider.querySelectorAll(".product-item__slide");
      if (slides.length <= 1) return;
      this.setupButtons();
      this.setupKeyboardNav();
      this.initObserver();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.loadTimers.forEach(timer => clearTimeout(timer));
      this.loadTimers.clear();
      this.prevBtn?.removeEventListener("click", this.handlePrev);
      this.nextBtn?.removeEventListener("click", this.handleNext);
      this.removeEventListener("keydown", this.handleKeydown);
    }
    setupButtons() {
      this.handlePrev = () => this.scrollToSlide(-1);
      this.handleNext = () => this.scrollToSlide(1);
      this.prevBtn.addEventListener("click", this.handlePrev);
      this.nextBtn.addEventListener("click", this.handleNext);
      this.prevBtn.setAttribute("aria-label", "Previous image");
      this.nextBtn.setAttribute("aria-label", "Next image");
    }
    setupKeyboardNav() {
      this.setAttribute("tabindex", "0");
      this.handleKeydown = e => {
        if (e.key === "ArrowLeft") this.scrollToSlide(-1);
        if (e.key === "ArrowRight") this.scrollToSlide(1);
      };
      this.addEventListener("keydown", this.handleKeydown);
    }
    scrollToSlide(direction) {
      if (!this.slideWidth) {
        this.slideWidth = this.slider.querySelector(".product-item__slide")?.clientWidth;
        if (!this.slideWidth) return;
      }
      this.slider.scrollBy({
        left: direction * this.slideWidth,
        behavior: "smooth"
      });
    }
    loadSlideImages(slide) {
      const lazyImg = slide.querySelector(".image__lazy");
      const blurImg = slide.querySelector(".image__blur");
      if (!lazyImg || !blurImg || lazyImg.classList.contains("is-loaded")) {
        return;
      }
      const revealImage = () => {
        blurImg.style.opacity = "0";
        lazyImg.classList.add("is-loaded");
      };
      if (lazyImg.complete) {
        lazyImg.style.display = "block";
        revealImage();
      } else {
        lazyImg.style.display = "block";
        lazyImg.addEventListener("load", revealImage, {
          once: true
        });
        lazyImg.addEventListener("error", () => {
          blurImg.style.opacity = "0";
        });
      }
    }
    preloadAdjacentSlides(currentSlide) {
      const executePreload = window.requestIdleCallback || (cb => setTimeout(cb, 100));
      const nextSlide = currentSlide.nextElementSibling;
      const prevSlide = currentSlide.previousElementSibling;
      if (nextSlide) {
        executePreload(() => this.loadSlideImages(nextSlide));
      }
      if (prevSlide) {
        executePreload(() => this.loadSlideImages(prevSlide));
      }
    }
    initObserver() {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const timer = setTimeout(() => {
              this.loadSlideImages(entry.target);
              this.preloadAdjacentSlides(entry.target);
              this.loadTimers.delete(entry.target);
            }, 300);
            this.loadTimers.set(entry.target, timer);
            this.updateA11y(entry.target);
          } else {
            const timer = this.loadTimers.get(entry.target);
            if (timer) {
              clearTimeout(timer);
              this.loadTimers.delete(entry.target);
            }
          }
        });
      }, {
        root: this.slider,
        threshold: 0.5
      });
      this.slider.querySelectorAll(".product-item__slide").forEach(slide => observer.observe(slide));
      this.observer = observer;
    }
    updateA11y(activeSlide) {
      const isFirst = !activeSlide.previousElementSibling;
      const isLast = !activeSlide.nextElementSibling;
      this.prevBtn.classList.toggle("is-disabled", isFirst);
      this.prevBtn.setAttribute("aria-disabled", isFirst);
      this.nextBtn.classList.toggle("is-disabled", isLast);
      this.nextBtn.setAttribute("aria-disabled", isLast);
    }
  });
}